/**
 * Altura Global — Analytics client.
 *
 * gtag.js + the Search Console meta tag are now baked into public/index.html
 * at build time via CRA's %REACT_APP_*% interpolation. That guarantees Google's
 * verification crawler + the initial HTML response contain them.
 *
 * This module handles what index.html cannot:
 *   - SPA page_view tracking on every React route change
 *   - Domain event helpers (resource_download, form_submit, etc.)
 *   - A defensive fallback: if the build-time %placeholder% did NOT get
 *     substituted (e.g. env var was missing at build), we clean up the broken
 *     meta tag / gtag script so the site stays valid HTML.
 */

const MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
const SITE_VERIFICATION = (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "").trim();

let _bootstrapped = false;

/** One-time initialization on app boot. */
export function initAnalytics() {
  if (_bootstrapped) return;
  _bootstrapped = true;

  // --- Search Console meta tag: repair or remove if the build-time
  // substitution left a literal `%REACT_APP_...%` placeholder.
  const metaEl = document.querySelector('meta[name="google-site-verification"]');
  if (metaEl) {
    const val = metaEl.getAttribute("content") || "";
    const looksLikePlaceholder = val.startsWith("%") && val.endsWith("%");
    if (looksLikePlaceholder && SITE_VERIFICATION) {
      metaEl.setAttribute("content", SITE_VERIFICATION);
    } else if (looksLikePlaceholder && !SITE_VERIFICATION) {
      metaEl.parentNode?.removeChild(metaEl);
    }
  } else if (SITE_VERIFICATION) {
    const m = document.createElement("meta");
    m.name = "google-site-verification";
    m.content = SITE_VERIFICATION;
    document.head.appendChild(m);
  }

  // --- gtag script: same cleanup for the placeholder-left-in-place case.
  const scriptEl = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  if (scriptEl) {
    const src = scriptEl.getAttribute("src") || "";
    if (src.includes("%REACT_APP_") && !MEASUREMENT_ID) {
      scriptEl.parentNode?.removeChild(scriptEl);
    }
  } else if (MEASUREMENT_ID) {
    // Runtime-only fallback if the static tag wasn't emitted.
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
    document.head.appendChild(s);
  }
}

/** Manual page_view event; call on every SPA route change. */
export function trackPageView(path, title) {
  if (!MEASUREMENT_ID || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: title || document.title,
  });
}

/** Generic event with custom params. */
export function trackEvent(name, params = {}) {
  if (!MEASUREMENT_ID || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

// ---- Domain-specific helpers used across the site --------------------------
export const track = {
  resourceDownload: (slug, title) =>
    trackEvent("resource_download", { resource_slug: slug, resource_title: title }),

  formSubmit: (form, extra = {}) =>
    trackEvent("form_submit", { form_name: form, ...extra }),

  consultationBooked: (extra = {}) =>
    trackEvent("consultation_booked", { ...extra }),

  globalLinkApply: () => trackEvent("global_link_apply"),

  auriChatStarted: () => trackEvent("auri_chat_started"),
  voiceAuriStarted: () => trackEvent("voice_auri_started"),
};

export const analyticsConfig = {
  measurementId: MEASUREMENT_ID,
  siteVerification: SITE_VERIFICATION,
  enabled: Boolean(MEASUREMENT_ID),
};
